/** AI B2B Leads: a button that downloads leads as CSV and says how it went. */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { downloadLeadsCsv, type ExportRequest } from "./export";

interface Props {
  request: ExportRequest;
  label: string;
  testId: string;
  disabled?: boolean;
  variant?: "default" | "outline" | "ghost";
}

export function ExportButton({ request, label, testId, disabled = false, variant = "outline" }: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const r = await downloadLeadsCsv(request);
      toast(r.total > r.rows
        ? { title: t("prospectLeads.export.partial", { shown: r.rows, total: r.total }) }
        : { title: t("prospectLeads.export.done", { count: r.rows }) });
    } catch (e: any) {
      toast({ title: t("prospectLeads.export.failed"), description: e?.message, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Button size="sm" variant={variant} onClick={run} disabled={disabled || busy} data-testid={testId}>
      {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : <Download className="mr-2 h-4 w-4" aria-hidden />}
      {busy ? t("prospectLeads.export.working") : label}
    </Button>
  );
}
